// Port of api/fuel/app/classes/insights/game/init.php
import Segment from 'offline/insights/segment'
import Util from 'offline/util'
import Constants from 'offline/constants'
import Game from 'offline/model/game'
import Collection from 'offline/model/collection'
import Rating from 'offline/model/rating'
import Comment from 'offline/model/comment'
import Version from 'offline/model/game/version'

export default Segment.extend({
    name: 'init',
    priority: 10,
    types: [Constants.TYPE_USER],
    run: function(type, owner, userIndex, detail, filter, vars) {

        var user_id = owner.get_id()
        var game_id = parseInt(vars.id, 10)

        // Load game model
        var game = Game.with_id(game_id)

        if (!game) return {}

        // Load collection record for this user+game
        var collection = Collection.record({
            where: { user_id: user_id, game_id: game_id }
        })

        // Load rating record for this user+game
        var rating = Rating.record({
            where: { user_id: user_id, game_id: game_id }
        })

        // Load comment for this user+game
        var comment = Comment.record({
            where: { user_id: user_id, game_id: game_id }
        })

        // Load all play records for this user+game ordered by date
        var playsRaw = Util.queryFast(
            'SELECT ' +
                'p.play_id AS play_id, ' +
                'p.game_id AS game_id, ' +
                'p.user_id AS user_id, ' +
                'p.play_date AS play_date, ' +
                'p.play_location AS play_location, ' +
                'p.play_length AS play_length, ' +
                'p.play_players AS play_players, ' +
                'p.play_count AS play_count, ' +
                'p.play_result AS play_result, ' +
                'p.play_won AS play_won, ' +
                'p.play_comment AS play_comment, ' +
                'p.play_incomplete AS play_incomplete, ' +
                'p.play_expansion AS play_expansion ' +
            'FROM play p ' +
            'WHERE p.user_id = ' + user_id + ' AND p.game_id = ' + game_id + ' ' +
            'ORDER BY p.play_date'
        )

        // Load all player records for those plays
        var playIds = Util.pluck(playsRaw, 'play_id')
        var playersRaw = []

        if (playIds.length) {
            playersRaw = Util.queryFast(
                'SELECT ' +
                    'p.player_id AS player_id, ' +
                    'p.play_id AS play_id, ' +
                    'p.user_id AS user_id, ' +
                    'p.player_name AS player_name, ' +
                    'p.player_username AS player_username, ' +
                    'p.player_start AS player_start, ' +
                    'p.player_color AS player_color, ' +
                    'p.player_score AS player_score, ' +
                    'p.player_new AS player_new, ' +
                    'p.player_rating AS player_rating, ' +
                    'p.player_win AS player_win ' +
                'FROM player p ' +
                'WHERE p.play_id IN(' + Util.in(playIds) + ')'
            )
        }

        // Load version if collection has one
        var version = null

        if (collection && collection.get_game_version_id()) {
            version = Version.with_id(collection.get_game_version_id())
        }

        // Calculate totals
        var totalPlays = 0
        var totalSessions = playsRaw.length

        for (var i = 0; i < playsRaw.length; i++) {
            totalPlays += parseInt(playsRaw[i].play_count, 10) || 0
        }

        // Set flags
        var has_collection = !!collection
        var has_rating = !!rating
        var has_plays = totalSessions > 0
        var has_comment = !!comment

        // Store data for dependent segments
        this._data.game = game
        this._data.collection = collection
        this._data.rating = rating
        this._data.comment = comment
        this._data.plays_raw = playsRaw
        this._data.players_raw = playersRaw
        this._data.version = version
        this._data.totalPlays = totalPlays
        this._data.totalSessions = totalSessions
        this._data.has_collection = has_collection
        this._data.has_rating = has_rating
        this._data.has_plays = has_plays
        this._data.has_comment = has_comment

        // Add game to references
        this._references.games.push(game_id)

        // Add parent game refs from game_base JSON
        var gameBase = game.get_base()
        if (gameBase) {
            if (typeof gameBase === 'string') {
                try { gameBase = JSON.parse(gameBase) } catch (e) { gameBase = null }
            }
            if (gameBase && Array.isArray(gameBase)) {
                for (var b = 0; b < gameBase.length; b++) {
                    if (gameBase[b] && gameBase[b].id) {
                        this._references.games.push(parseInt(gameBase[b].id, 10))
                    }
                }
            }
        }

        // Pre-compute user's full game universe (base games only)
        var universeRows = Util.queryFast(
            'SELECT c.game_id AS id FROM collection c JOIN game g ON g.game_id = c.game_id WHERE c.user_id = ' + user_id + ' AND g.game_type = ' + Constants.GAME_TYPE_BASE +
            ' UNION ' +
            'SELECT p.game_id AS id FROM play p JOIN game g ON g.game_id = p.game_id WHERE p.user_id = ' + user_id + ' AND g.game_type = ' + Constants.GAME_TYPE_BASE +
            ' UNION ' +
            'SELECT r.game_id AS id FROM rating r JOIN game g ON g.game_id = r.game_id WHERE r.user_id = ' + user_id + ' AND g.game_type = ' + Constants.GAME_TYPE_BASE +
            ' UNION ' +
            'SELECT DISTINCT l.game_id AS id FROM log_user_collection_status l JOIN game g ON g.game_id = l.game_id' +
            ' WHERE l.user_id = ' + user_id +
            ' AND l.log_status = ' + Constants.COLLECTION_STATUS_OWN +
            ' AND l.log_context = ' + Constants.LOG_CONTEXT_ADDED +
            ' AND g.game_type = ' + Constants.GAME_TYPE_BASE
        )

        this._data.universe_ids = Util.pluck(universeRows, 'id')

        // Game metadata: designers and primary publishers
        var designerRows = Util.queryFast(
            'SELECT d.designer_name AS name FROM game_designer gd JOIN designer d ON d.designer_id = gd.designer_id WHERE gd.game_id = ' + game_id
        )
        var designers = Util.pluck(designerRows, 'name')

        var publisherRows = Util.queryFast(
            'SELECT p.publisher_name AS name FROM game_publisher gp JOIN publisher p ON p.publisher_id = gp.publisher_id WHERE gp.game_id = ' + game_id + ' AND gp.game_publisher_primary = 1 AND gp.game_version_id = 0'
        )
        var publishers = Util.pluck(publisherRows, 'name')

        return {
            game: {
                id: game_id,
                name: game.get_name(),
                type: game.get_type(),
                year: parseInt(game.get_published(), 10) || 0,
                rank: parseInt(game.get_rank(), 10) || 0,
                playersMin: parseInt(game.get_players_min(), 10) || 0,
                playersMax: parseInt(game.get_players_max(), 10) || 0,
                timeMin: parseInt(game.get_time_min(), 10) || 0,
                timeMax: parseInt(game.get_time_max(), 10) || 0,
                designers: designers,
                publishers: publishers,
                has_collection: has_collection,
                has_rating: has_rating,
                has_plays: has_plays,
                has_comment: has_comment,
                totalPlays: totalPlays,
                totalSessions: totalSessions
            }
        }

    }
})
